/*
 * ARTEMIS CLO (ajout maison, voir assets/CUSTOM.txt)
 * "You'll love these" dans le tiroir panier Fullstack.
 * - alimente par l'API Shopify de recommandations sur le DERNIER article ajoute
 *   (intent=complementary d'abord, puis related en complement) ;
 * - pose SOUS les articles, dans la zone qui defile (le tiroir n'a pas de zone de blocs a cet endroit) ;
 * - panier vide : ne touche a rien ;
 * - ajout en un clic : /cart/add.js en demandant la section du tiroir (+ auto-gifts),
 *   puis CartUpdatedEvent avec `sections` : le tiroir se re-rend lui-meme (barre de livraison comprise).
 * Reglages : section "custom-code" du groupe cart-drawer-group (#ac-cart-suggestions-config).
 */
import { CartUpdatedEvent } from '@theme/events';

const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const recCache = new Map(); // productId -> Promise<products[]>
let lastNode = null;
let lastKey = '';
let busy = false;
let again = false;

function cfg() {
  const el = document.getElementById('ac-cart-suggestions-config');
  const d = (el && el.dataset) || {};
  return {
    enabled: d.enabled !== 'false',
    title: d.title || "You'll love these",
    min: parseInt(d.min || '6', 10),
    max: parseInt(d.max || '10', 10),
    add: d.addLabel || 'Add',
    choose: d.chooseLabel || 'Size',
    pick: d.pickLabel || 'Choose a size',
  };
}

function money(cents, currency) {
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency: currency || 'GBP' }).format(cents / 100);
  } catch (e) {
    return (cents / 100).toFixed(2);
  }
}

function img(url, w) {
  if (!url) return '';
  const u = url.startsWith('//') ? 'https:' + url : url;
  return u + (u.includes('?') ? '&' : '?') + 'width=' + w;
}

async function fetchRecs(productId, intent, limit) {
  try {
    const r = await fetch(`${ROOT}recommendations/products.json?product_id=${productId}&limit=${limit}&intent=${intent}`);
    if (!r.ok) return [];
    const j = await r.json();
    return j.products || [];
  } catch (e) {
    console.warn('[ac-cart-suggestions]', intent, e);
    return [];
  }
}

function getRecs(productId, max) {
  if (!recCache.has(productId)) {
    recCache.set(
      productId,
      (async () => {
        // les deux appels partent en parallele : complementary d'abord, related en complement
        const [comp, rel] = await Promise.all([fetchRecs(productId, 'complementary', 10), fetchRecs(productId, 'related', 10)]);
        const seen = new Set(comp.map((p) => p.id));
        return comp.concat(rel.filter((p) => !seen.has(p.id)));
      })(),
    );
  }
  return recCache.get(productId);
}

function variantLabel(product, variant) {
  // n'affiche que les options qui ont plusieurs valeurs (ex. la taille, pas un coloris unique)
  const opts = product.options || [];
  const parts = [];
  opts.forEach((name, i) => {
    const key = 'option' + (i + 1);
    const values = new Set(product.variants.map((v) => v[key]));
    if (values.size > 1) parts.push(variant[key]);
  });
  return parts.join(' / ') || variant.title;
}

function esc(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

function build(products, c, currency) {
  const wrap = document.createElement('div');
  wrap.className = 'ac-cs';
  wrap.setAttribute('data-ac-cart-suggestions', '');
  const cards = products
    .map((p) => {
      const avail = p.variants.filter((v) => v.available);
      if (!avail.length) return '';
      const one = avail.length === 1 && p.variants.length === 1;
      const v0 = avail[0];
      const compare = p.compare_at_price && p.compare_at_price > p.price ? `<s class="ac-cs__compare">${money(p.compare_at_price, currency)}</s>` : '';
      const select = one
        ? `<input type="hidden" name="id" value="${v0.id}">`
        : `<div class="select-wrapper select-wrapper--full-width ac-cs__select-wrap"><select class="select select--full-width ac-cs__select" name="id" aria-label="${esc(c.choose)}">
             <option value="" selected disabled>${esc(c.choose)}</option>
             ${avail.map((v) => `<option value="${v.id}">${esc(variantLabel(p, v))}</option>`).join('')}
           </select></div>`;
      return `<li class="ac-cs__card">
        <a class="ac-cs__media" href="${esc(p.url)}" tabindex="-1">${p.featured_image ? `<img src="${img(p.featured_image, 240)}" alt="${esc(p.title)}" width="120" height="150" loading="lazy">` : ''}</a>
        <a class="ac-cs__title" href="${esc(p.url)}">${esc(p.title)}</a>
        <span class="ac-cs__price">${money(p.price, currency)} ${compare}</span>
        <form class="ac-cs__form" data-product-id="${p.id}">
          ${select}
          <button type="submit" class="button button--secondary button--small button--full-width ac-cs__add"><span>${esc(c.add)}</span></button>
        </form>
      </li>`;
    })
    .filter(Boolean)
    .slice(0, c.max);
  if (!cards.length) return null;
  wrap.innerHTML = `<p class="ac-cs__heading h6">${esc(c.title)}</p><ul class="ac-cs__list" role="list">${cards.join('')}</ul>`;
  wrap.addEventListener('submit', onSubmit);
  wrap.addEventListener('change', (e) => {
    if (e.target.matches('.ac-cs__select')) e.target.closest('.ac-cs__select-wrap').classList.remove('ac-cs__select-wrap--error');
  });
  return wrap;
}

async function onSubmit(e) {
  e.preventDefault();
  const form = e.target;
  const input = form.querySelector('[name=id]');
  const btn = form.querySelector('button');
  const c = cfg();
  if (!input.value) {
    // une taille a choisir : on ouvre la liste au lieu d'ajouter au hasard
    const w = form.querySelector('.ac-cs__select-wrap');
    w && w.classList.add('ac-cs__select-wrap--error');
    try { input.showPicker ? input.showPicker() : input.focus(); } catch (_) { input.focus(); }
    return;
  }
  const drawer = document.querySelector('cart-drawer');
  const sections = ['auto-gifts'];
  if (drawer) sections.push(drawer.dataset.sectionId);
  btn.classList.add('is-loading');
  btn.disabled = true;
  try {
    const r = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(input.value), quantity: 1 }], sections: sections.join(','), sections_url: window.location.pathname }),
    });
    const data = await r.json();
    if (!r.ok || data.status) {
      document.dispatchEvent(new CustomEvent('toast:open', { detail: { type: 'error', message: data.description || data.message || 'Error' } }));
      return;
    }
    const count = document.querySelector('cart-icon')?.getCurrentItemCount?.();
    document.dispatchEvent(
      new CartUpdatedEvent({}, { itemCount: count != null ? count + 1 : undefined, source: 'ac-cart-suggestions', sections: data.sections }),
    );
  } catch (err) {
    console.error('[ac-cart-suggestions]', err);
  } finally {
    btn.classList.remove('is-loading');
    btn.disabled = false;
  }
}

function place(node) {
  const drawer = document.querySelector('cart-drawer');
  const body = drawer && drawer.querySelector('.cart-drawer__body');
  if (!body) return false;
  body.querySelectorAll('[data-ac-cart-suggestions]').forEach((n) => n !== node && n.remove());
  if (node && node.parentNode !== body) body.appendChild(node);
  return true;
}

function cartIsEmpty() {
  const drawer = document.querySelector('cart-drawer');
  if (!drawer) return true;
  return drawer.classList.contains('cart-drawer--empty') || !drawer.querySelector('cart-drawer-item');
}

async function refresh() {
  if (busy) { again = true; return; }
  busy = true;
  try {
    const c = cfg();
    if (!c.enabled || cartIsEmpty()) {
      // panier vide : on retire seulement ce qu'on a pose nous-memes
      document.querySelectorAll('[data-ac-cart-suggestions]').forEach((n) => n.remove());
      return;
    }
    if (lastNode) place(lastNode); // remise immediate, sans clignotement, avant la mise a jour

    const cart = await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    const items = (cart.items || []).filter((i) => !(i.properties && i.properties._is_gift === 'true') && i.price > 0);
    if (!items.length) {
      document.querySelectorAll('[data-ac-cart-suggestions]').forEach((n) => n.remove());
      lastNode = null; lastKey = '';
      return;
    }
    const anchor = items[0]; // Shopify liste les articles du plus recent au plus ancien
    const inCart = new Set(cart.items.map((i) => i.product_id));
    const key = anchor.product_id + '|' + [...inCart].sort().join(',');
    if (key !== lastKey || !lastNode) {
      const recs = (await getRecs(anchor.product_id, c.max)).filter((p) => !inCart.has(p.id) && p.available);
      const node = build(recs, c, cart.currency);
      document.querySelectorAll('[data-ac-cart-suggestions]').forEach((n) => n !== node && n.remove());
      lastNode = node; lastKey = key;
    }
    if (lastNode && !cartIsEmpty()) place(lastNode);
  } catch (e) {
    console.warn('[ac-cart-suggestions]', e);
  } finally {
    busy = false;
    if (again) { again = false; refresh(); }
  }
}

function watch() {
  const drawer = document.querySelector('cart-drawer');
  if (!drawer) return;
  const inner = drawer.querySelector('.cart-drawer__inner');
  // le tiroir remplace tout son contenu a chaque mise a jour : on se repose apres chaque rendu
  new MutationObserver(() => refresh()).observe(inner, { childList: true });
  new MutationObserver(() => refresh()).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  refresh();
  // fiche produit : on prepare les suggestions du produit consulte, pretes s'il est ajoute
  const pid = window.ShopifyAnalytics?.meta?.product?.id || document.querySelector('product-recommendations[data-product-id]')?.dataset.productId;
  if (pid) {
    const warm = () => getRecs(Number(pid), cfg().max);
    'requestIdleCallback' in window ? requestIdleCallback(warm, { timeout: 4000 }) : setTimeout(warm, 2000);
  }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watch);
else watch();
